import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HotToastService } from '@ngxpert/hot-toast';
import { VersionService } from '../../../services/version.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-about-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './about-dialog.component.html',
  styleUrls: ['./about-dialog.component.scss']
})
export class AboutDialogComponent implements OnInit {
  private readonly versionService = inject(VersionService);
  private readonly toast = inject(HotToastService);
  public readonly dialogRef = inject(MatDialogRef<AboutDialogComponent>);

  public readonly appVersion = this.versionService.appVersion;
  public readonly appCommit = this.versionService.appCommit;
  public readonly environmentLabel = environment.environmentLabel;
  public readonly browser = this.detectBrowser();
  public readonly system = this.detectOs();

  public serverVersion = 'Loading…';
  public serverCommit: string | null = null;

  ngOnInit(): void {
    this.versionService.getServerVersion().subscribe(v => {
      this.serverVersion = v.version;
      this.serverCommit = v.commit;
    });
  }

  public get appDisplay(): string {
    // Staging/development builds derive their version from `git describe`
    // (e.g. "1.2.2-5-g3715083"), which looks like a release but isn't — show
    // only the commit there to avoid confusion. Production shows the version.
    if (this.environmentLabel) {
      return this.appCommit || this.appVersion;
    }
    return this.appCommit && this.appCommit !== 'local'
      ? `${this.appVersion} (${this.appCommit})`
      : this.appVersion;
  }

  public get serverDisplay(): string {
    if (this.environmentLabel) {
      return this.serverCommit ?? this.serverVersion;
    }
    return this.serverCommit
      ? `${this.serverVersion} (${this.serverCommit})`
      : this.serverVersion;
  }

  public copy(): void {
    navigator.clipboard.writeText(this.buildReport()).then(
      () => this.toast.success('Copied'),
      () => this.toast.error('Could not copy')
    );
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  private buildReport(): string {
    const lines = ['NRG Metrics', `App: ${this.appDisplay}`, `Server: ${this.serverDisplay}`];
    if (this.environmentLabel) {
      lines.push(`Environment: ${this.environmentLabel}`);
    }
    lines.push(`Browser: ${this.browser} / ${this.system}`);
    return lines.join('\n');
  }

  private detectBrowser(): string {
    const ua = navigator.userAgent;
    // Order matters: Edge/Opera UA strings also contain "Chrome"; Chrome contains "Safari".
    const tests: { name: string; re: RegExp }[] = [
      { name: 'Edge', re: /edg(?:e|a|ios)?\/(\d+)/i },
      { name: 'Opera', re: /opr\/(\d+)/i },
      { name: 'Chrome', re: /chrome\/(\d+)/i },
      { name: 'Firefox', re: /firefox\/(\d+)/i },
      { name: 'Safari', re: /version\/(\d+)[\d.]* safari/i }
    ];
    for (const t of tests) {
      const m = t.re.exec(ua);
      if (m) {
        return `${t.name} ${m[1]}`;
      }
    }
    return ua || 'Unknown';
  }

  private detectOs(): string {
    const ua = navigator.userAgent;
    // Windows 10 and 11 both report "Windows NT 10.0" — not distinguishable from UA alone.
    if (/windows/i.test(ua)) return 'Windows';
    if (/mac os x/i.test(ua)) return 'macOS';
    if (/android/i.test(ua)) return 'Android';
    if (/iphone|ipad|ipod/i.test(ua)) return 'iOS';
    if (/linux/i.test(ua)) return 'Linux';
    return 'Unknown';
  }
}
